/* eslint-disable ember/no-computed-properties-in-native-classes */
// Extends From
import Controller from '@ember/controller';
// Ember Imports
import { tracked } from '@glimmer/tracking';
import { get, set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
// Internal Imports
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import PermissionType from '@linc-technologies/linced-common/enums/events/permission-type';

export default class PermissionsController extends Controller {
  PermissionType = PermissionType;
  @tracked permissionFilters = [];
  @tracked optionFilters = [];
  @tracked templateFilters = [];
  @tracked riskFilters = [];
  @tracked mitigationFilters = [];

  @computed('model.permissions.@each.type')
  get permissions() {
    return this.model.permissions.filter(
      (item) => item.type === PermissionType.PERMISSION,
    );
  }

  @computed('model.permissions.@each.type')
  get options() {
    return this.model.permissions.filter(
      (item) => item.type === PermissionType.OPTION,
    );
  }

  @computed('permissions.[]', 'permissionFilters.@each.{field,value}')
  get filteredPermissions() {
    if (isEmpty(this.permissionFilters)) {
      // No filters - just return the data.
      return this.permissions;
    }

    // Determine datatypes for any specified filters.
    let filters = this.permissionFilters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.permissions.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('model.templates.[]', 'templateFilters.@each.{field,value}')
  get filteredTemplates() {
    if (isEmpty(this.templateFilters)) {
      // No filters - just return the data.
      return this.model.templates;
    }

    // Determine datatypes for any specified filters.
    let filters = this.templateFilters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.model.templates.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('model.risks.[]', 'riskFilters.@each.{field,value}')
  get filteredRisks() {
    if (isEmpty(this.riskFilters)) {
      // No filters - just return the data.
      return this.model.risks;
    }

    // Determine datatypes for any specified filters.
    let filters = this.riskFilters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.model.risks.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('model.mitigations.[]', 'mitigationFilters.@each.{field,value}')
  get filteredMitigations() {
    if (isEmpty(this.mitigationFilters)) {
      // No filters - just return the data.
      return this.model.mitigations;
    }

    // Determine datatypes for any specified filters.
    let filters = this.mitigationFilters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.model.mitigations.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('options.[]', 'optionFilters.@each.{field,value}')
  get filteredOptions() {
    if (isEmpty(this.optionFilters)) {
      // No filters - just return the data.
      return this.options;
    }

    // Determine datatypes for any specified filters.
    let filters = this.optionFilters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.options.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @action
  filter(field, type, value) {
    let key = 'templateFilters';
    switch (type) {
      case PermissionType.PERMISSION:
        key = 'permissionFilters';
        break;
      case PermissionType.OPTION:
        key = 'optionFilters';
        break;
      case 'risk':
        key = 'riskFilters';
        break;
      case 'mitigation':
        key = 'mitigationFilters';
        break;
    }
    let filters = this[key];
    let filter = filters.find((item) => item.field === field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this[key] = [...filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this[key] = filters.filter((item) => item !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  onRiskSaved(risk) {
    let mitigations = risk.get('mitigations');
    mitigations.forEach((mitigation) => {
      mitigation.set('inUse', true);
    });
  }
}
